import React, { useState, useEffect, useRef, useMemo, useCallback, memo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { 
  Search,
  RefreshCw,
  LayoutGrid,
  List,
  Filter,
  ChevronRight,
  ChevronLeft,
  Phone,
  MessageSquare,
  Send,
  CheckCircle,
  Clock,
  AlertTriangle,
  User,
  Wrench,
  MapPin,
  ExternalLink,
  Smartphone
} from 'lucide-react';
import { StatusChip, PriorityChip, SLABadge } from '@/components/phs/StatusChip';
import { computeWorkflowStatus } from '@/components/phs/WorkflowStatus';
import { PHSCommandLayout } from '@/components/phs/enterprise';
import { withWyldecrestAuth } from '@/pages/wyldecrest/WyldecrestAuthGuard';
import { useDebounce, useIsMobile, useVirtualizedList } from '@/hooks/usePerformance';

const API = process.env.REACT_APP_BACKEND_URL;

// Virtualization config
const CARD_HEIGHT_STANDARD = 140; // px for standard cards
const CARD_HEIGHT_COMPACT = 100; // px for compact cards
const VIRTUALIZATION_THRESHOLD = 10; // Only virtualize columns with more than this many cards

// Board columns configuration - 15 status columns
const BOARD_COLUMNS = [
  { id: 'new', label: 'New Enquiries', statuses: ['New', 'Enquiry', 'Job Created'], color: 'bg-red-500' },
  { id: 'assigned', label: 'Assigned', statuses: ['Assigned', 'Pending Acceptance'], color: 'bg-blue-500' },
  { id: 'awaiting_quote', label: 'Awaiting Quote', statuses: ['Awaiting Quote', 'Quote Requested'], color: 'bg-amber-500' },
  { id: 'quote_review', label: 'Quote Review', statuses: ['Quote Submitted', 'Quote Received', 'Quote Awaiting Review'], color: 'bg-amber-600' },
  { id: 'quote_approval', label: 'Awaiting Approval', statuses: ['Quote Awaiting Approval'], color: 'bg-orange-500' },
  { id: 'approved', label: 'Approved', statuses: ['Approved', 'Quote Approved'], color: 'bg-green-500' },
  { id: 'scheduled', label: 'Scheduled', statuses: ['Scheduled', 'Works Ordered', 'PO Issued'], color: 'bg-teal-500' },
  { id: 'in_progress', label: 'In Progress', statuses: ['In Progress', 'Works In Progress'], color: 'bg-blue-600' },
  { id: 'completed_review', label: 'Completed Review', statuses: ['Completed', 'Awaiting Review', 'Completed Awaiting Review'], color: 'bg-teal-600' },
  { id: 'awaiting_invoice', label: 'Awaiting Invoice', statuses: ['Awaiting Invoice'], color: 'bg-purple-500' },
  { id: 'invoiced', label: 'Invoiced', statuses: ['Invoiced', 'Invoice Submitted'], color: 'bg-purple-600' },
  { id: 'paid', label: 'Paid', statuses: ['Paid', 'Invoice Approved'], color: 'bg-green-600' },
  { id: 'closed', label: 'Completed', statuses: ['Closed'], color: 'bg-slate-500' },
  { id: 'cancelled', label: 'Cancelled', statuses: ['Cancelled'], color: 'bg-red-600' },
  { id: 'follow_on', label: 'Follow-On Required', statuses: ['Follow On Quote Required', 'Follow On Required'], color: 'bg-indigo-500' }
];

// Compact Job Card for Board - MOBILE OPTIMIZED
// Memoized for performance
const BoardJobCard = memo(function BoardJobCard({ job, onClick, onQuickAction, isActionLoading, compactMode = false }) {
  const navigate = useNavigate();
  const workflowStatus = computeWorkflowStatus(job);
  
  const residentName = job.resident_name || job.contact_name || 'Unknown';
  const contractorName = job.contractor_name || job.assigned_contractor?.company_name || 'Unassigned';
  const parkName = job.park_name || job.park || '';
  const plotNumber = job.plot_number || job.plot || '';
  
  // Check if specific action is loading
  const isChaseLoading = isActionLoading?.('chase', job.id);
  
  // Calculate SLA urgency
  const getSLAClass = () => {
    if (!job.sla_due) return '';
    const now = new Date();
    const due = new Date(job.sla_due);
    const hoursRemaining = (due - now) / (1000 * 60 * 60);
    if (hoursRemaining < 0) return 'border-l-4 border-l-red-500';
    if (hoursRemaining < 24) return 'border-l-4 border-l-amber-500';
    return '';
  };
  
  // Compact mode for mobile - slimmer cards
  if (compactMode) {
    return (
      <div
        className={`bg-white rounded-lg border border-slate-200 p-2.5 cursor-pointer active:bg-slate-50 touch-manipulation ${getSLAClass()}`}
        onClick={() => onClick?.(job)}
        data-testid={`board-card-${job.id}`}
      >
        <div className="flex items-center justify-between mb-1.5">
          <span className="text-[11px] font-semibold text-slate-700">{job.reference || job.id?.slice(0, 8)}</span>
          <PriorityChip priority={job.priority} size="xs" />
        </div>
        <div className="text-[10px] text-slate-500 truncate mb-1.5">
          {parkName}{plotNumber ? ` / ${plotNumber}` : ''}
        </div>
        <div className="flex items-center justify-between text-[9px]">
          <span className="text-slate-400 truncate max-w-[60%]">{workflowStatus.awaiting_with}</span>
          {job.sla_due && <SLABadge job={job} size="xs" />}
        </div>
        {/* Mobile Quick Actions - Always visible, touch-optimized */}
        <div className="flex gap-1.5 mt-2 pt-2 border-t border-slate-100">
          <button
            onClick={(e) => { e.stopPropagation(); onQuickAction?.('chase', job); }}
            disabled={isChaseLoading}
            className="flex-1 flex items-center justify-center h-9 rounded-lg bg-blue-50 text-blue-600 text-[10px] active:bg-blue-100 touch-manipulation"
            aria-label="Chase"
          >
            {isChaseLoading ? <RefreshCw className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); navigate(`/wyldecrest/helpdesk/job/${job.id}`); }}
            className="flex-1 flex items-center justify-center h-9 rounded-lg bg-slate-100 text-slate-600 text-[10px] active:bg-slate-200 touch-manipulation"
            aria-label="Open"
          >
            <ExternalLink className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
    );
  }
  
  // Standard desktop card
  return (
    <div
      className={`bg-white rounded-xl border border-slate-200 p-3 cursor-pointer hover:shadow-md hover:border-slate-300 transition group ${getSLAClass()}`}
      onClick={() => onClick?.(job)}
      data-testid={`board-card-${job.id}`}
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-semibold text-slate-700">{job.reference || job.id?.slice(0, 8)}</span>
        <PriorityChip priority={job.priority} size="xs" />
      </div>
      
      {/* Location */}
      <div className="flex items-center gap-1 text-[11px] text-slate-500 mb-2">
        <MapPin className="h-3 w-3" />
        <span className="truncate">{parkName}{plotNumber ? ` / ${plotNumber}` : ''}</span>
      </div>
      
      {/* Resident & Contractor */}
      <div className="grid grid-cols-2 gap-2 mb-2 text-[10px]">
        <div className="flex items-center gap-1 text-slate-600">
          <User className="h-2.5 w-2.5 text-slate-400" />
          <span className="truncate">{residentName}</span>
        </div>
        <div className="flex items-center gap-1 text-slate-600">
          <Wrench className="h-2.5 w-2.5 text-slate-400" />
          <span className="truncate">{contractorName}</span>
        </div>
      </div>
      
      {/* SLA Badge */}
      {job.sla_due && (
        <div className="mb-2">
          <SLABadge job={job} size="xs" />
        </div>
      )}
      
      {/* Awaiting With */}
      <div className="flex items-center justify-between text-[10px] border-t border-slate-100 pt-2 mt-2">
        <div>
          <span className="text-slate-400">Awaiting: </span>
          <span className="font-medium text-slate-600">{workflowStatus.awaiting_with}</span>
        </div>
      </div>
      
      {/* Quick Actions - Show on Hover - Touch-optimized buttons */}
      <div className="hidden group-hover:flex gap-1 mt-2 pt-2 border-t border-slate-100">
        <button
          onClick={(e) => { e.stopPropagation(); onQuickAction?.('chase', job); }}
          disabled={isChaseLoading}
          className={`flex-1 flex items-center justify-center gap-1 min-h-[32px] rounded text-[10px] transition touch-manipulation
            ${isChaseLoading ? 'bg-blue-100 text-blue-400 cursor-not-allowed' : 'bg-blue-50 text-blue-600 hover:bg-blue-100 active:bg-blue-200'}`}
          title="Chase"
          aria-label="Chase contractor"
        >
          {isChaseLoading ? (
            <RefreshCw className="h-3 w-3 animate-spin" />
          ) : (
            <Send className="h-3 w-3" />
          )}
        </button>
        <button
          onClick={(e) => { e.stopPropagation(); onQuickAction?.('message', job); }}
          className="flex-1 flex items-center justify-center gap-1 min-h-[32px] rounded bg-emerald-50 text-emerald-600 text-[10px] hover:bg-emerald-100 active:bg-emerald-200 touch-manipulation"
          title="Message"
          aria-label="Open messages"
        >
          <MessageSquare className="h-3 w-3" />
        </button>
        <button
          onClick={(e) => { e.stopPropagation(); navigate(`/wyldecrest/helpdesk/job/${job.id}`); }}
          className="flex-1 flex items-center justify-center gap-1 min-h-[32px] rounded bg-slate-50 text-slate-600 text-[10px] hover:bg-slate-100 active:bg-slate-200 touch-manipulation"
          title="Open"
          aria-label="Open job details"
        >
          <ExternalLink className="h-3 w-3" />
        </button>
      </div>
    </div>
  );
});

// Board Column - MOBILE OPTIMIZED with Virtualization for Large Datasets
const BoardColumn = memo(function BoardColumn({ column, jobs, onCardClick, onQuickAction, isActionLoading, compactMode }) {
  const count = jobs.length;
  const containerRef = useRef(null);
  const [containerHeight, setContainerHeight] = useState(400);
  
  // Calculate item height based on mode
  const itemHeight = compactMode ? CARD_HEIGHT_COMPACT : CARD_HEIGHT_STANDARD;
  
  // Only use virtualization for columns with many items
  const useVirtualization = count > VIRTUALIZATION_THRESHOLD;
  
  // Virtualization hook
  const { 
    visibleItems, 
    containerStyle, 
    onScroll 
  } = useVirtualizedList(jobs, itemHeight, containerHeight, 3);
  
  // Measure container height
  useEffect(() => {
    if (!containerRef.current) return;
    
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setContainerHeight(entry.contentRect.height);
      }
    });
    
    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);
  
  return (
    <div 
      className={`flex-shrink-0 flex flex-col h-full ${compactMode ? 'w-52' : 'w-64'}`} 
      data-testid={`board-column-${column.id}`}
    >
      {/* Column Header - Sticky */}
      <div className="sticky top-0 z-10 bg-slate-100 px-2 py-2 rounded-t-xl border-b border-slate-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className={`w-2 h-2 rounded-full ${column.color}`} />
            <span className={`font-semibold text-slate-700 ${compactMode ? 'text-[10px]' : 'text-xs'}`}>
              {compactMode ? column.label.split(' ')[0] : column.label}
            </span>
          </div>
          <Badge variant="secondary" className="text-[10px] h-5 min-w-[20px] justify-center">
            {count}
          </Badge>
        </div>
      </div>
      
      {/* Column Content - Virtualized for large lists */}
      <div 
        ref={containerRef}
        className="flex-1 overflow-y-auto p-1.5 bg-slate-50 rounded-b-xl scroll-smooth overscroll-contain"
        onScroll={useVirtualization ? onScroll : undefined}
      >
        {jobs.length === 0 ? (
          <div className="text-center py-6 text-xs text-slate-400">
            No jobs
          </div>
        ) : useVirtualization ? (
          // Virtualized rendering for large columns
          <div style={containerStyle}>
            {visibleItems.map(({ item: job, style }) => (
              <div key={job.id} style={style} className="px-0.5 py-0.75">
                <BoardJobCard
                  job={job}
                  onClick={onCardClick}
                  onQuickAction={onQuickAction}
                  isActionLoading={isActionLoading}
                  compactMode={compactMode}
                />
              </div>
            ))}
          </div>
        ) : (
          // Standard rendering for small columns
          <div className="space-y-1.5">
            {jobs.map(job => (
              <BoardJobCard
                key={job.id}
                job={job}
                onClick={onCardClick}
                onQuickAction={onQuickAction}
                isActionLoading={isActionLoading}
                compactMode={compactMode}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
});

// Board View Component
function OperationalBoardView({ wyldecrestUser }) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [refreshing, setRefreshing] = useState(false);
  // C2 FIX: Track in-progress actions to prevent duplicates
  const [actionInProgress, setActionInProgress] = useState({});
  // Mobile mode toggle
  const [compactMode, setCompactMode] = useState(false);
  const boardRef = useRef(null);
  
  // Performance hooks
  const isMobile = useIsMobile();
  const debouncedSearch = useDebounce(searchTerm, 300);
  
  const token = localStorage.getItem('wyldecrest_token') || localStorage.getItem('token');
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  
  // Auto-enable compact mode on mobile
  useEffect(() => {
    if (isMobile && !compactMode) {
      setCompactMode(true);
    }
  }, [isMobile]);
  
  useEffect(() => {
    loadJobs();
  }, []);
  
  // C6 FIX: Use filter_type=all for board view to get all active/completed/cancelled jobs
  const loadJobs = useCallback(async (showRefreshing = false) => {
    if (showRefreshing) setRefreshing(true);
    else setLoading(true);
    
    try {
      const res = await axios.get(`${API}/api/wyldecrest/jobs?filter_type=all&limit=500`, { headers });
      setJobs(res.data.jobs || res.data || []);
    } catch (err) {
      console.error('Failed to load jobs:', err);
      toast.error('Failed to load jobs');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [headers]);
  
  // Filter jobs by debounced search term (performance optimization)
  const filteredJobs = useMemo(() => {
    if (!debouncedSearch) return jobs;
    const search = debouncedSearch.toLowerCase();
    return jobs.filter(job => 
      job.reference?.toLowerCase().includes(search) ||
      job.id?.toLowerCase().includes(search) ||
      job.resident_name?.toLowerCase().includes(search) ||
      job.contact_name?.toLowerCase().includes(search) ||
      job.park_name?.toLowerCase().includes(search) ||
      job.park?.toLowerCase().includes(search) ||
      job.contractor_name?.toLowerCase().includes(search)
    );
  }, [jobs, debouncedSearch]);
  
  // M3 FIX: Improved status matching with exact match priority and word boundary matching
  const columnJobs = useMemo(() => {
    const grouped = {};
    // Normalize status for comparison
    const normalizeStatus = (s) => s.toLowerCase().replace(/[\s_-]+/g, '_').trim();
    
    BOARD_COLUMNS.forEach(col => {
      const normalizedColStatuses = col.statuses.map(normalizeStatus);
      
      grouped[col.id] = filteredJobs.filter(job => {
        const status = job.status || '';
        const normalizedJobStatus = normalizeStatus(status);
        
        // Priority 1: Exact match after normalization
        if (normalizedColStatuses.includes(normalizedJobStatus)) {
          return true;
        }
        
        // Priority 2: Check if any column status exactly matches start of job status (word boundary)
        // This prevents "Completed" from matching "Completed Awaiting Review" incorrectly
        return normalizedColStatuses.some(colStatus => {
          // Exact match after normalization
          if (colStatus === normalizedJobStatus) return true;
          
          // Check if job status starts with column status and next char is word boundary
          if (normalizedJobStatus.startsWith(colStatus)) {
            const nextChar = normalizedJobStatus[colStatus.length];
            // If no next char or next char is a word boundary, it's a match
            return !nextChar || nextChar === '_';
          }
          
          return false;
        });
      });
    });
    return grouped;
  }, [filteredJobs]);
  
  // Column counts for stats
  const stats = useMemo(() => {
    let total = 0, overdue = 0, dueToday = 0;
    filteredJobs.forEach(job => {
      total++;
      if (job.sla_due) {
        const now = new Date();
        const due = new Date(job.sla_due);
        const hoursRemaining = (due - now) / (1000 * 60 * 60);
        if (hoursRemaining < 0) overdue++;
        else if (hoursRemaining < 24) dueToday++;
      }
    });
    return { total, overdue, dueToday };
  }, [filteredJobs]);
  
  const handleCardClick = (job) => {
    navigate(`/wyldecrest/helpdesk/job/${job.id}`);
  };
  
  // C2 FIX: Duplicate action prevention with loading state
  const handleQuickAction = async (action, job) => {
    const actionKey = `${action}-${job.id}`;
    
    // Prevent duplicate actions
    if (actionInProgress[actionKey]) {
      return;
    }
    
    setActionInProgress(prev => ({ ...prev, [actionKey]: true }));
    
    try {
      switch (action) {
        case 'chase':
          await axios.post(`${API}/api/phs/jobs/${job.id}/communications`, {
            type: 'contractor_message',
            message: 'Please provide an update on this job.',
            visibility: 'contractor'
          }, { headers });
          toast.success('Chase message sent');
          break;
        case 'message':
          navigate(`/wyldecrest/helpdesk/job/${job.id}?tab=communications`);
          break;
        default:
          navigate(`/wyldecrest/helpdesk/job/${job.id}`);
      }
    } catch (err) {
      console.error('Quick action failed:', err);
      // Handle rate limiting error specifically
      if (err.response?.status === 429) {
        toast.error(err.response?.data?.detail || 'Rate limit exceeded. Please wait before sending more messages.');
      } else {
        toast.error('Action failed');
      }
    } finally {
      setActionInProgress(prev => ({ ...prev, [actionKey]: false }));
    }
  };
  
  // Helper to check if action is in progress (passed to cards)
  const isActionInProgress = useCallback((action, jobId) => {
    return !!actionInProgress[`${action}-${jobId}`];
  }, [actionInProgress]);
  
  // Mobile scroll handlers
  const scrollBoard = useCallback((direction) => {
    if (boardRef.current) {
      const scrollAmount = compactMode ? 220 : 280;
      boardRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  }, [compactMode]);

  // Handle logout
  const handleLogout = () => {
    localStorage.removeItem('wyldecrest_token');
    localStorage.removeItem('wyldecrest_user');
    window.location.href = '/admin/login';
  };
  
  return (
    <PHSCommandLayout
      user={wyldecrestUser}
      onLogout={handleLogout}
      jobCounts={{ pipeline: jobs.length }}
      notifications={[]}
    >
      <div className="h-[calc(100vh-64px)] flex flex-col" data-testid="operational-board-view">
        {/* Header */}
        <div className="flex-shrink-0 bg-white border-b border-slate-200 px-3 sm:px-4 py-2 sm:py-3">
          <div className="flex items-center justify-between mb-2 sm:mb-3">
            <div>
              <h1 className="text-base sm:text-lg font-semibold text-slate-800">Operational Pipeline</h1>
              <p className="text-[10px] sm:text-xs text-slate-500">Workflow control board</p>
            </div>
            <div className="flex items-center gap-1.5 sm:gap-2">
              {/* Mobile Mode Toggle */}
              <Button 
                variant={compactMode ? "default" : "outline"}
                size="sm"
                onClick={() => setCompactMode(!compactMode)}
                className="h-8 px-2 sm:px-3"
                title={compactMode ? "Standard view" : "Compact mobile view"}
              >
                <Smartphone className="h-3.5 w-3.5 sm:mr-1.5" />
                <span className="hidden sm:inline">{compactMode ? "Standard" : "Compact"}</span>
              </Button>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => loadJobs(true)} 
                disabled={loading || refreshing}
                className="h-8"
              >
                <RefreshCw className={`h-3.5 w-3.5 sm:mr-1.5 ${refreshing ? 'animate-spin' : ''}`} />
                <span className="hidden sm:inline">Refresh</span>
              </Button>
            </div>
          </div>
          
          {/* Stats & Search - Mobile Optimized */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
            <div className="flex items-center gap-3 sm:gap-4 text-[10px] sm:text-xs overflow-x-auto">
              <div className="flex items-center gap-1 shrink-0">
                <span className="text-slate-500">Total:</span>
                <span className="font-semibold text-slate-700">{stats.total}</span>
              </div>
              {stats.overdue > 0 && (
                <div className="flex items-center gap-1 shrink-0">
                  <AlertTriangle className="h-3 w-3 text-red-500" />
                  <span className="font-semibold text-red-600">{stats.overdue} Overdue</span>
                </div>
              )}
              {stats.dueToday > 0 && (
                <div className="flex items-center gap-1 shrink-0">
                  <Clock className="h-3 w-3 text-amber-500" />
                  <span className="font-semibold text-amber-600">{stats.dueToday} Due</span>
                </div>
              )}
            </div>
            
            <div className="relative w-full sm:w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search jobs..."
                className="pl-9 h-9 sm:h-8 text-sm"
                data-testid="board-search"
              />
            </div>
          </div>
        </div>
        
        {/* Board - MOBILE OPTIMIZED with scroll controls */}
        <div className="flex-1 flex flex-col relative overflow-hidden">
          {/* Mobile Scroll Controls */}
          {isMobile && (
            <>
              <button
                onClick={() => scrollBoard('left')}
                className="absolute left-0 top-1/2 -translate-y-1/2 z-20 w-8 h-16 bg-white/90 border border-slate-200 rounded-r-lg flex items-center justify-center shadow-md active:bg-slate-50 touch-manipulation"
                aria-label="Scroll left"
              >
                <ChevronLeft className="h-5 w-5 text-slate-600" />
              </button>
              <button
                onClick={() => scrollBoard('right')}
                className="absolute right-0 top-1/2 -translate-y-1/2 z-20 w-8 h-16 bg-white/90 border border-slate-200 rounded-l-lg flex items-center justify-center shadow-md active:bg-slate-50 touch-manipulation"
                aria-label="Scroll right"
              >
                <ChevronRight className="h-5 w-5 text-slate-600" />
              </button>
            </>
          )}
          
          <div 
            ref={boardRef}
            className="flex-1 overflow-x-auto overflow-y-hidden p-2 sm:p-4 scroll-smooth overscroll-x-contain"
            style={{ 
              scrollbarWidth: 'thin',
              scrollbarColor: '#94a3b8 #f1f5f9',
              WebkitOverflowScrolling: 'touch'
            }}
          >
            {loading ? (
              <div className={`flex h-full ${compactMode ? 'gap-2' : 'gap-4'}`}>
                {[1, 2, 3, 4, 5, 6].map(i => (
                  <div key={i} className={`flex-shrink-0 ${compactMode ? 'w-52' : 'w-64'}`}>
                    <Skeleton className="h-10 rounded-t-xl mb-2" />
                    <div className={`space-y-${compactMode ? '1.5' : '2'}`}>
                      <Skeleton className={`${compactMode ? 'h-24' : 'h-32'} rounded-xl`} />
                      <Skeleton className={`${compactMode ? 'h-24' : 'h-32'} rounded-xl`} />
                      <Skeleton className={`${compactMode ? 'h-24' : 'h-32'} rounded-xl`} />
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className={`flex h-full min-w-max ${compactMode ? 'gap-2' : 'gap-4'}`}>
                {BOARD_COLUMNS.map(column => (
                  <BoardColumn
                    key={column.id}
                    column={column}
                    jobs={columnJobs[column.id] || []}
                    onCardClick={handleCardClick}
                    onQuickAction={handleQuickAction}
                    isActionLoading={isActionInProgress}
                    compactMode={compactMode}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </PHSCommandLayout>
  );
}

export default withWyldecrestAuth(OperationalBoardView, 'helpdesk');

export { BOARD_COLUMNS, BoardJobCard };
